import React, { useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import styled from 'styled-components'

import useDefer, { Status } from '../../common/hooks/useDefer'
import { loadUsers } from '../load-users'
import Text from '../../ui/Text'
import { Button } from '../button'
import { CurrentUser } from '../currentUser'
import { addPartnerAdmin } from '../add-partner-admin'
import { SInput } from '../../support/features/charge-point-capi-details-widget/charge-point-capi-details-widget.style'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../table'
import useText from '../../common/hooks/useText'

export const SSearchForm = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 5px;
`

export const SSearchInput = styled(SInput)`
    flex: 1;
`

export const SAddButton = styled(Button)`
    padding: 3px;
    border-radius: 3px;
`

export type AddPartnerAdminModalProps = {
    adminPartners: Array<CurrentUser>
    onLoadPartnerAdmin: () => void
    partnerId: string
    close: () => void
    className?: ClassName
    style?: React.CSSProperties
}

export const AddPartnerAdminModal = React.memo(function AddPartnerAdminModal({
    adminPartners,
    onLoadPartnerAdmin,
    partnerId,
    close,
    className,
    style,
}: AddPartnerAdminModalProps): React.ReactElement {
    const text = useText()
    const [email, setEmail] = useState('')
    const adminPartnersIds = adminPartners.map((partner) => partner.id)

    const {
        execute: findUsers,
        value: users,
        status: findingUsersStatus,
    } = useDefer(
        () =>
            loadUsers({ noOfItems: 50, email }).then((users) =>
                users.filter((user) => !adminPartnersIds.includes(user.id))
            ),
        [email, adminPartners]
    )

    const { execute: addPartner } = useDefer(
        (userId) =>
            addPartnerAdmin(partnerId, userId).then(() => {
                onLoadPartnerAdmin()
                close()
            }),
        [partnerId]
    )

    return (
        <div className={cn(className)} style={style}>
            <div className="modal-header" data-testid="nedPartnerHeader">
                <Text code="lblPartnerAdminModal" />
            </div>
            <div className="modal-body">
                <SSearchForm>
                    <SSearchInput
                        placeholder={text('lblEmailField')}
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                    />
                    <Button
                        loading={findingUsersStatus === Status.PENDING}
                        onClick={findUsers}
                        title={text('btnSearch')}
                        data-testid="searchBtn"
                        small
                    />
                    <Button small onClick={close} title={text('btnCancel')} data-testid="cancelBtn" />
                </SSearchForm>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell data-testid="theadNameModal">
                                <Text code="lblName" />
                            </TableHeaderCell>
                            <TableHeaderCell data-testid="theadEmailModal">
                                <Text code="lblEmail" />
                            </TableHeaderCell>
                            <TableHeaderCell data-testid="theadAddModal">
                                <Text code="lblAdd" />
                            </TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {users?.map((user, i) => (
                            <TableRow key={Math.random()}>
                                <TableBodyCell data-testid={'userName-' + i}>
                                    {user.firstName} {user.lastName}
                                </TableBodyCell>
                                <TableBodyCell data-testid={'userEmail-' + i}>{user.email}</TableBodyCell>
                                <TableBodyCell>
                                    <SAddButton
                                        tooltip={text('btnAdd')}
                                        onClick={() => addPartner(user.id)}
                                        title={<FontAwesomeIcon icon={faPlus} />}
                                        data-testid={'userAdd-' + i}
                                    />
                                </TableBodyCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
})
